/// <reference path="../../globals.d.ts" />

import Vue, { CreateElement } from '@src/vue'
import { Component, Watch } from 'vue-property-decorator'
import User from '@src/data/user'
import { Spacer } from '@src/elements/spacer/spacer'
import { Divider } from '@src/elements/divider/divider'
import { Sidebar, NavItem, ProfileNavItem } from '@src/components/sidebar/sidebar'
import { Logo } from '@src/elements/logo/logo'
import { DefaultValues } from '@src/util/default-values'
import { ExerciseResponse } from '@src/data/exercise-response'
import { ClubService } from '@src/service/club'
import { convertDateTo } from '@src/util/date-format'
import { ExerciseService } from '@src/service/exercise'
import 'element-ui/lib/theme-chalk/index.css'

import './platform.sass'
import { PortraitHeader } from '@src/components/header/portrait'
import SessionManager from '@src/lib/session_manager'
import { PortraitSettings } from '@src/components/portrait-settings'
import { Icon } from '@src/elements/icon/icon'
import { Subtitle1 } from '@src/elements/typography/typography'
import { NotificationTypes } from '@src/util/constants'
import { ConfirmModal } from '@src/elements/modal/confirm-modal'

@Component({
  computed: {
    user () {
      return this.$store.state.user
    },
    exercises () {
      return this.$store.state.availableExercises
    },
    locale () {
      return this.$store.state.i18n.locale
    },
    isSidebarVisible () {
      return this.$store.state.isSidebarVisible
    }
  }
})
export class PlatformRoute extends Vue {

  user: User

  private exercises: ExerciseResponse[]

  private locale: string

  private isSidebarVisible: boolean

  private logoutConfirmVisible: boolean = false

  private portraitSettingsAreVisible: boolean = false

  constructor () {
    super()
  }

  goToRoute (path) {
    return () => {
      this.$store.dispatch('updateSidebarVisibility', false)
      this.$router.push(path)
    }
  }

  isActiveRoute (path) {
    return !!this.$route.path.match(path)
  }

  @Watch('isSidebarVisible')
  closePortraitSettings () {
    if (this.portraitSettingsAreVisible && !this.isSidebarVisible) {
      this.portraitSettingsAreVisible = false
    }
  }

  goToAccount () {
    this.$router.push('/trainers/platform/account')
    this.portraitSettingsAreVisible = false
    this.$store.dispatch('updateSidebarVisibility', false)
  }

  showLogoutConfirm () {
    this.logoutConfirmVisible = !this.logoutConfirmVisible
  }

  updatePortraitSettingsState () {
    this.portraitSettingsAreVisible = !this.portraitSettingsAreVisible
  }

  logout (confirm: boolean) {
    if (confirm === true) {
      SessionManager.Instance.logout()
      this.$store.dispatch('logout')
      this.$store.dispatch('updateSidebarVisibility', false)
      this.$router.push('/')
    }
    this.logoutConfirmVisible = false
  }

  closeSideBar () {
    this.$store.dispatch('updateSidebarVisibility', false)
  }

  render (h: CreateElement) {
    let japanese = this.locale === 'ja-jp'
    return (
      <div class='platform-root'>

        <PortraitHeader />
        <div class='platform-page'>
          <div class={`sidebar-container member-sidebar ${this.isSidebarVisible ? 'open' : 'close'}`}>
            <Sidebar>
              <Logo type='responsive' />

              <NavItem icon={'sidebar/members'} label={this.$t('USERS')}
                       click={this.goToRoute('/trainers/platform/members')}
                       selected={this.isActiveRoute('/trainers/platform/members')} />
              <NavItem icon={'sidebar/exercise'} label={this.$t('MY_CONTENT')}
                       click={this.goToRoute('/trainers/platform/content/workouts')}
                       selected={this.isActiveRoute('/trainers/platform/content')} />
              {SessionManager.Instance.isOwner() && <NavItem icon={'sidebar/stations'} label={this.$t('MY_CLUB')}
                       click={this.goToRoute('/trainers/platform/club/arrangement')}
                       selected={this.isActiveRoute('/trainers/platform/club')} />}
              {/*<NavItem icon={'sidebar/tasks'} label={this.$t('TASKS')}*/}
              {/*click={this.goToRoute('/trainers/platform/tasks')}*/}
              {/*selected={this.isActiveRoute('/trainers/platform/tasks')} />*/}
              <NavItem icon={'sidebar/messaging'} label={this.$t('PAGE.MESSAGING.TITLE')}
                       click={this.goToRoute('/trainers/platform/messaging')}
                       selected={this.isActiveRoute('/trainers/platform/messaging')} />
              {!japanese && <NavItem icon={'sidebar/blog'} label={this.$t('PAGE.BLOG.MENU_TITLE')}
                                     click={this.goToRoute('/trainers/platform/blog')}
                                     selected={this.isActiveRoute('/trainers/platform/blog')} />}
              <NavItem icon={'sidebar/support'} label={this.$t('SUPPORT.SECTION')}
                       click={this.goToRoute('/trainers/platform/support/contact')}
                       selected={this.isActiveRoute('/trainers/platform/support/contact') || this.isActiveRoute('/trainers/platform/support')} />

              <Spacer />

              <Divider slot={'bottom'} />

              <ProfileNavItem label={this.user.fullName} slot={'bottom'} />
              <PortraitSettings label={this.user.fullName} callback={this.updatePortraitSettingsState} slot={'bottom'} />

            </Sidebar>
          </div>

          <div class='content' onClick={this.closeSideBar}>
            <router-view />
          </div>

          <div
            class='version-release-date'>{WEB_RELEASE_DATE ? `Release date: ${convertDateTo(WEB_RELEASE_DATE, 'DD/MM/YYYY HH:mm')}` : /* istanbul ignore next: WEB_RELEASE_DATE is a build constant, only empty in production builds */ ''}
          </div>

          <ConfirmModal show={this.logoutConfirmVisible}
                        closeCallback={this.logout}
                        message={this.$t('PAGE.LOGOUT.CONFIRM')}
                        type={NotificationTypes.CONFIRM} />

          {this.portraitSettingsAreVisible && (
            <div class='portrait-settings-options'>
              <div class='settings-back-arrow-wrapper'>
                <Icon name={'util/back-arrow'} class='logo' click={this.updatePortraitSettingsState} />
              </div>
              <div onClick={this.goToAccount} class='profile-option' ref='accountBtn'>
                <Subtitle1>{this.$t('PAGE.MEMBERS.ACCOUNT.ACCOUNT_SETTINGS')}</Subtitle1>
              </div>
              <div onClick={this.showLogoutConfirm} class='profile-option' ref='logoutBtn'>
                <Subtitle1>{this.$t('PAGE.LOGOUT.SUBMIT_BUTTON')}</Subtitle1>
              </div>
            </div>
          )}
        </div>
    </div>
    )
  }

  created () {
    this.$watch('locale', () => this.translateExerciseNames())

    const loadPromises = [
      // club settings are needed to compute different duration computation on multiple child components of this one
      ClubService.loadClub(this.$store),
      DefaultValues.loadExerciseTags(this.$store),
      DefaultValues.loadActivities(this.$store),
      this.loadAllExercises(),
      DefaultValues.loadPredefinedHealthGoals(this.$store)
    ]

    return Promise.all(loadPromises).then(() => this.translateExerciseNames())
  }

  private loadAllExercises () {
    if (this.$store.state.availableExercises.length === 0) {
      return new ExerciseService().loadAllExercises().then(async (exercises) => {
        await this.$store.dispatch('setAvailableExercises', exercises)
      })
    }
  }

  private translateExerciseNames () {
    if (!this.$i18n || !this.exercises) return

    let exercises = this.exercises.map(exercise => {
      if (!this.$te('EXERCISE[' + exercise.number + '].NAME')) exercise.setTranslatedName('')

      if (this.$te('EXERCISE[' + exercise.number + '].NAME')) {
        exercise.setTranslatedName(this.$t('EXERCISE[' + exercise.number + '].NAME'))
      }
      return exercise
    })
    this.$store.dispatch('setAvailableExercises', exercises)
  }
}
